import { useSyncExternalStore } from 'react';

import {
	PING_GRACE_MS,
	type PopoutChannel,
	type PopoutMessage,
	STALE_MS,
	createPopoutChannel,
	frontPopoutWindow,
} from './popoutChannel';

/**
 * App-wide view of which dashboard widgets currently live in their own
 * window, for pages other than the dashboard (orders row menu, navbar scale
 * pills). One shared channel, started by the first subscriber and closed by
 * the last. Tracks ids only; window handles stay with usePopoutWidgets.
 */

const PRUNE_INTERVAL_MS = 1000;
const EMPTY: string[] = [];

let channel: PopoutChannel | null = null;
let unsubscribe: (() => void) | null = null;
let prune: ReturnType<typeof setInterval> | null = null;
let lastSeen: Record<string, { seen: number; pinged: boolean }> = {};
let ids: string[] = EMPTY;
const listeners = new Set<() => void>();

const publish = () => {
	const next = Object.keys(lastSeen).sort();
	const same =
		next.length === ids.length && next.every((id, i) => id === ids[i]);
	if (same) return;
	ids = next.length === 0 ? EMPTY : next;
	for (const l of listeners) l();
};

const start = () => {
	if (channel) return;
	channel = createPopoutChannel();
	if (!channel) return;
	unsubscribe = channel.subscribe((msg) => {
		if (msg.type === 'opened' || msg.type === 'heartbeat') {
			lastSeen[msg.widgetId] = { seen: Date.now(), pinged: false };
			publish();
		} else if (msg.type === 'closed') {
			delete lastSeen[msg.widgetId];
			publish();
		}
	});
	channel.post({ type: 'ping' });
	prune = setInterval(() => {
		const cutoff = Date.now() - STALE_MS;
		let ping = false;
		for (const [id, entry] of Object.entries(lastSeen)) {
			if (entry.seen >= cutoff) continue;
			// Heartbeats from a hidden window are throttled; ping once and wait
			// out the grace before dropping the entry (see PING_GRACE_MS).
			if (!entry.pinged) {
				entry.pinged = true;
				entry.seen = cutoff + PING_GRACE_MS;
				ping = true;
				continue;
			}
			delete lastSeen[id];
		}
		if (ping) channel?.post({ type: 'ping' });
		publish();
	}, PRUNE_INTERVAL_MS);
};

const stop = () => {
	if (prune) clearInterval(prune);
	prune = null;
	unsubscribe?.();
	unsubscribe = null;
	channel?.close();
	channel = null;
	lastSeen = {};
	publish();
};

const subscribe = (listener: () => void) => {
	listeners.add(listener);
	start();
	return () => {
		listeners.delete(listener);
		if (listeners.size === 0) stop();
	};
};

const getSnapshot = () => ids;
const getEmpty = () => EMPTY;
const subscribeNothing = () => () => {};

/**
 * Sorted ids of widgets that are live in a popup window right now. Pass
 * `enabled = false` where the answer is never used (row menus outside the
 * orders table) so the shared channel is not started for nothing.
 */
export const usePoppedOutIds = (enabled = true) =>
	useSyncExternalStore(
		enabled ? subscribe : subscribeNothing,
		enabled ? getSnapshot : getEmpty,
		getEmpty,
	);

const post = (message: PopoutMessage) => {
	if (channel) {
		channel.post(message);
		return;
	}
	// No subscriber keeps the store alive; post through a throwaway channel.
	// Closing right after postMessage is safe: the message is already queued
	// on every other channel's event loop before close() returns.
	const tmp = createPopoutChannel();
	if (!tmp) return;
	tmp.post(message);
	tmp.close();
};

/** Bring a widget's popup to the front from any page. */
export const focusPopoutWindow = (id: string) => {
	frontPopoutWindow(id);
	post({ type: 'focus-request', widgetId: id });
};

/** Hand an order to a popped-out weighing widget and front its window. */
export const forwardWeighOrderToPopout = (id: string, orderId: number) => {
	post({ type: 'weigh-order', widgetId: id, orderId });
	focusPopoutWindow(id);
};

/** Test helper: drop the shared channel and all tracked ids. */
export const resetPopoutLiveness = () => {
	listeners.clear();
	stop();
};
